import { RetrievalMethod } from '@/feature/profiles/constants';
import { RequestActionFormsData } from '@/feature/patients/types';
import { Separator } from '@predoc/cow';

interface RequestSummaryItemProps {
  title: string;
  children: React.ReactNode;
}

export const RequestSummaryItem = ({
  title,
  children,
}: RequestSummaryItemProps) => {
  return (
    <div>
      <div className="text-muted-foreground text-sm font-medium">{title}</div>
      {children}
    </div>
  );
};

interface RequestSummaryDetailProps {
  children: React.ReactNode;
}

export const RequestSummaryDetail = ({
  children,
}: RequestSummaryDetailProps) => {
  return <div>{children}</div>;
};

interface RequestMethodSummaryProps {
  selectedMethods: RetrievalMethod[];
  actionFormsData: RequestActionFormsData;
}

export const RequestMethodSummary = ({
  selectedMethods,
  actionFormsData,
}: RequestMethodSummaryProps) => {
  // display order - Provider/Facility should be first
  const displayOrder = [
    RetrievalMethod.PROVIDER_FACILITY,
    RetrievalMethod.HIE,
    RetrievalMethod.MEDICATIONS,
    RetrievalMethod.FACESHEETS,
    RetrievalMethod.RECORD_UPLOAD,
  ];

  // only show selected methods in the correct order
  const methodsToShow = displayOrder.filter((method) =>
    selectedMethods.includes(method),
  );

  const renderMethodContent = (method: RetrievalMethod) => {
    switch (method) {
      case RetrievalMethod.PROVIDER_FACILITY: {
        const providerData = actionFormsData[RetrievalMethod.PROVIDER_FACILITY];

        return (
          <RequestSummaryItem title="Provider/Facility Retrieval">
            {/* Show providers/facilities from modal if available */}
            {providerData?.retrieveFrom &&
            providerData.retrieveFrom.length > 0 ? (
              providerData.retrieveFrom.map((provider, index) => {
                const parts = [];
                if (provider.providerName) parts.push(provider.providerName);
                if (provider.facilityName) parts.push(provider.facilityName);

                const recordsRequestedMap = {
                  complete: 'Complete Record Retrieval',
                  specific: 'Specific Medical Records',
                };
                if (provider.recordsRequested) {
                  parts.push(
                    `(${recordsRequestedMap[provider.recordsRequested]})`,
                  );
                }

                return (
                  <RequestSummaryDetail key={index}>
                    {parts.join(' - ')}
                  </RequestSummaryDetail>
                );
              })
            ) : (
              /* Fallback to requestFrom (root level field) if no modal data available */
              <RequestSummaryDetail>
                {providerData?.requestFrom}
              </RequestSummaryDetail>
            )}
          </RequestSummaryItem>
        );
      }

      case RetrievalMethod.HIE: {
        const hieData = actionFormsData[RetrievalMethod.HIE];
        return (
          <RequestSummaryItem title="Digital Health Data">
            {hieData?.alternativePostalCodes &&
            hieData.alternativePostalCodes.length > 0 ? (
              hieData.alternativePostalCodes.map((zipCode, index) => (
                <RequestSummaryDetail key={index}>
                  {zipCode}
                </RequestSummaryDetail>
              ))
            ) : (
              <RequestSummaryDetail>
                No ZIP codes specified
              </RequestSummaryDetail>
            )}
          </RequestSummaryItem>
        );
      }

      case RetrievalMethod.MEDICATIONS:
        return (
          <RequestSummaryItem title="Medications Data">
            <RequestSummaryDetail>Medications Data</RequestSummaryDetail>
          </RequestSummaryItem>
        );

      case RetrievalMethod.RECORD_UPLOAD: {
        const uploadData = actionFormsData[RetrievalMethod.RECORD_UPLOAD];
        return (
          <RequestSummaryItem title="Medical Record Upload">
            {uploadData?.uploadedFiles &&
            uploadData.uploadedFiles.length > 0 ? (
              uploadData.uploadedFiles.map((file, index) => (
                <RequestSummaryDetail key={index}>
                  {file.filename}
                </RequestSummaryDetail>
              ))
            ) : (
              <RequestSummaryDetail>No files uploaded</RequestSummaryDetail>
            )}
          </RequestSummaryItem>
        );
      }

      case RetrievalMethod.FACESHEETS: {
        const facesheetData = actionFormsData[RetrievalMethod.FACESHEETS];
        return (
          <RequestSummaryItem title="Facesheets">
            {facesheetData?.selectedFacesheets &&
              facesheetData.selectedFacesheets.length > 0 && (
                <>
                  {facesheetData.selectedFacesheets.map((facesheet) => (
                    <RequestSummaryDetail key={facesheet.facesheetConfigId}>
                      {facesheet.displayName}
                    </RequestSummaryDetail>
                  ))}
                </>
              )}
          </RequestSummaryItem>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      {methodsToShow.map((method, index) => {
        const content = renderMethodContent(method);
        const isLastMethod = index === methodsToShow.length - 1;

        return (
          <div key={method}>
            {content}
            {!isLastMethod && <Separator className="my-3" />}
          </div>
        );
      })}
    </>
  );
};
